import type { RefObject } from "react"
import { useEffect, useRef } from "react"
import { t } from "i18next"
import cn from "clsx"
import { Icon, palette, Typography } from "@callcenter/design-system"
import { useGate, useUnit } from "effector-react"
import { $whiteLabel } from "@callcenter/white-label"
import { AuthServiceInstance, LogoutMode, UserService } from "@callcenter/services"
import { useCustomOnClickOutside, useToggleDisplay } from "@callcenter/hooks"
import { $userService } from "@callcenter/effector-services"

import { userAccountModel } from "../../models"
import { UserAccount } from "../user-account"

import styles from "./user-profile-popup.module.pcss"

type Props = {
  userProfilePicRef?: RefObject<HTMLElement | null>
  topPosition?: string
}

export const UserProfilePopup = ({ userProfilePicRef, topPosition = "56px" }: Props) => {
  const { display, toggleDisplay, close } = useToggleDisplay()
  const popupRef = useRef<HTMLUListElement>(null)
  const { docsUrl } = useUnit($whiteLabel.$whiteLabelSettings)

  const getUserPic = () => userProfilePicRef?.current ?? document.getElementById("user-profile-pic")

  useGate(userAccountModel.HeaderGate)

  const userSettings = useUnit($userService.$userSettings)

  const actions = useUnit({
    userAccountOpened: userAccountModel.userAccountOpened
  })

  const onUserPickClick = (event: MouseEvent) => {
    const target: Node = event.target as Node

    if (!popupRef.current?.contains(target)) {
      getUserPic()?.classList.toggle("active")
      toggleDisplay()
    }
  }

  const onAccountSettingsClick = () => {
    actions.userAccountOpened()
    UserService.getCallcenterAndUserSettings()
    getUserPic()?.classList.toggle("active")
    close()
  }

  useEffect(() => {
    const element = getUserPic()

    element?.addEventListener("click", onUserPickClick)

    return () => {
      element?.removeEventListener("click", onUserPickClick)
    }
  }, [display])

  useCustomOnClickOutside(popupRef, onUserPickClick)

  const onSignOutClick = () => {
    if (window.isAuthByTokenSession) {
      AuthServiceInstance.logout(LogoutMode.tokenApplication)
    } else {
      AuthServiceInstance.logout()
    }
  }

  return (
    <>
      {display && (
        <ul className={cn("dropdown-clipped", styles.container)} style={{ top: topPosition }} ref={popupRef}>
          <li className={styles.info}>
            <Typography.TopHeader weight="medium" className={styles.infoName}>
              {userSettings.name}
            </Typography.TopHeader>
            <Typography.Body2 className={styles.email}>{userSettings.email}</Typography.Body2>
          </li>

          <li>
            <Typography.TopHeader
              as="a"
              weight="regular"
              className={styles.externalLink}
              onClick={onAccountSettingsClick}
            >
              <Icon icon={Icon.icons.system.s.utilities.settings} color={palette.gray10} size={16} />
              {t("userProfile_MenuBar_userProfileMenuOptions_accountSettingsLabel")}
            </Typography.TopHeader>
          </li>

          <li>
            <Typography.TopHeader
              as="a"
              weight="regular"
              className={styles.externalLink}
              href={docsUrl}
              target="_blank"
            >
              <Icon icon={Icon.icons.system.s.utilities.help} color={palette.gray10} size={16} />
              {t("userProfile_MenuBar_userProfileMenuOptions_documentationPortalLabel")}
            </Typography.TopHeader>
          </li>

          <li className={styles.divider} />

          <li>
            <Typography.TopHeader as="a" weight="regular" className={styles.externalLink} onClick={onSignOutClick}>
              <Icon icon={Icon.icons.system.s.utilities.signOut} color={palette.gray10} size={16} />
              {t("userProfile_MenuBar_userProfileMenuOptions_signOutLabel")}
            </Typography.TopHeader>
          </li>
        </ul>
      )}
      <UserAccount />
    </>
  )
}
